/**
 * useFormState — V2.I.9
 *
 * Minimal form-state hook : tracks values, per-field errors, dirty/touched
 * state, and centralises the validation/submit cycle.
 *
 * Why : every form in the app reimplements its own `[name, setName] +
 * [error, setError] + [isSubmitting] + handleSubmit` machinery, with
 * inconsistent error-display styles. This hook gives a single contract
 * the `<FormField>` wrapper can consume to render errors uniformly.
 *
 * Design notes :
 *  - Validators run on submit AND on `setValue` for already-touched
 *    fields (so the user gets fast feedback once they've interacted).
 *  - `touched` flips to true on `blur` (or `setTouched(name)` manually).
 *  - The hook stays storage-agnostic — it doesn't auto-bind to inputs.
 *    Callers wire the `register(name)` helpers themselves to keep
 *    flexibility (some forms use selects, custom widgets, etc.).
 *
 * Example :
 *   const form = useFormState({
 *     initial: { name: '', email: '' },
 *     validate: {
 *       name: (v) => v.trim().length === 0 ? 'Nom requis' : null,
 *       email: (v) => /^[^@]+@[^@]+$/.test(v) ? null : 'Email invalide',
 *     },
 *   });
 *
 *   <FormField label="Nom" error={form.errorIfTouched('name')}>
 *     <input
 *       value={form.values.name}
 *       onChange={(e) => form.setValue('name', e.target.value)}
 *       onBlur={() => form.setTouched('name')}
 *     />
 *   </FormField>
 *
 *   <button onClick={async () => {
 *     if (!form.validateAll()) return;
 *     await save(form.values);
 *   }}>Save</button>
 */

import { useCallback, useState } from 'react';

export type Validator<V> = (value: V, allValues: Record<string, unknown>) => string | null;

export interface UseFormStateOptions<T extends Record<string, unknown>> {
  /** Initial values. Both the keys and the types are inferred. */
  initial: T;
  /** Per-field validator. Return `null` if valid, an error message otherwise. */
  validate?: Partial<{ [K in keyof T]: Validator<T[K]> }>;
}

export interface UseFormStateReturn<T extends Record<string, unknown>> {
  values: T;
  errors: Partial<Record<keyof T, string>>;
  touched: Partial<Record<keyof T, boolean>>;

  /** Replace one field's value. Also re-validates IF touched. */
  setValue: <K extends keyof T>(name: K, value: T[K]) => void;
  /** Mark a field as touched (call on blur) and re-validate. */
  setTouched: <K extends keyof T>(name: K, touched?: boolean) => void;
  /** Set or clear an error externally (server-side validation). */
  setError: <K extends keyof T>(name: K, error: string | null) => void;

  /** Run all validators. Returns true when no errors. */
  validateAll: () => boolean;

  /** Reset to the initial values. */
  reset: (next?: Partial<T>) => void;

  /**
   * Returns the field error only when the field has been touched. Most
   * callers want this — showing "required" on a virgin field is hostile.
   */
  errorIfTouched: <K extends keyof T>(name: K) => string | undefined;

  /** True when at least one validator currently reports an error. */
  hasErrors: boolean;
}

export function useFormState<T extends Record<string, unknown>>(
  options: UseFormStateOptions<T>,
): UseFormStateReturn<T> {
  const { initial, validate } = options;

  const [values, setValues] = useState<T>(initial);
  const [errors, setErrors] = useState<Partial<Record<keyof T, string>>>({});
  const [touched, setTouchedState] = useState<Partial<Record<keyof T, boolean>>>({});

  const runValidator = useCallback(
    <K extends keyof T>(name: K, value: T[K], allValues: T): string | null => {
      const v = validate?.[name];
      if (!v) return null;
      return v(value, allValues as Record<string, unknown>);
    },
    [validate],
  );

  const setValue = useCallback(
    <K extends keyof T>(name: K, value: T[K]) => {
      setValues((prev) => {
        const next = { ...prev, [name]: value };
        // Re-validate IF the field was already touched (live feedback).
        setErrors((prevErrors) => {
          if (!touched[name]) return prevErrors;
          const err = runValidator(name, value, next);
          const updated = { ...prevErrors };
          if (err) updated[name] = err;
          else delete updated[name];
          return updated;
        });
        return next;
      });
    },
    [touched, runValidator],
  );

  const setTouched = useCallback(
    <K extends keyof T>(name: K, isTouched = true) => {
      setTouchedState((prev) => ({ ...prev, [name]: isTouched }));
      // Validate on blur — but only when a validator exists for this
      // field. If there's no validator, leave any externally-set error
      // (e.g. from `setError(...)` for server-side validation) alone.
      if (!validate?.[name]) return;
      setErrors((prevErrors) => {
        const err = runValidator(name, values[name], values);
        const updated = { ...prevErrors };
        if (err) updated[name] = err;
        else delete updated[name];
        return updated;
      });
    },
    [runValidator, values, validate],
  );

  const setError = useCallback(<K extends keyof T>(name: K, error: string | null) => {
    setErrors((prev) => {
      const updated = { ...prev };
      if (error) updated[name] = error;
      else delete updated[name];
      return updated;
    });
  }, []);

  const validateAll = useCallback((): boolean => {
    const next: Partial<Record<keyof T, string>> = {};
    let ok = true;
    (Object.keys(values) as Array<keyof T>).forEach((key) => {
      const err = runValidator(key, values[key], values);
      if (err) {
        next[key] = err;
        ok = false;
      }
    });
    setErrors(next);
    // Mark all fields as touched on submit so errors surface.
    const allTouched: Partial<Record<keyof T, boolean>> = {};
    (Object.keys(values) as Array<keyof T>).forEach((k) => {
      allTouched[k] = true;
    });
    setTouchedState(allTouched);
    return ok;
  }, [runValidator, values]);

  const reset = useCallback(
    (next?: Partial<T>) => {
      setValues({ ...initial, ...(next || {}) });
      setErrors({});
      setTouchedState({});
    },
    [initial],
  );

  const errorIfTouched = useCallback(
    <K extends keyof T>(name: K): string | undefined => {
      return touched[name] ? errors[name] : undefined;
    },
    [touched, errors],
  );

  return {
    values,
    errors,
    touched,
    setValue,
    setTouched,
    setError,
    validateAll,
    reset,
    errorIfTouched,
    hasErrors: Object.keys(errors).length > 0,
  };
}
